import { useQuery } from '@tanstack/react-query'
import { Download, Loader2, Printer } from 'lucide-react'

import { api } from '@/api/client'
import type { Supplier, SupplierOrder } from '@/types/models'
import { inputCls } from '@/components/Field'
import { cn, formatNum } from '@/lib/utils'
import { useFileDownload } from '@/lib/useFileDownload'
import { useTabField } from '@/stores/tabState'

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/** 회사관리 > 구매장 > 협력업체 발주서 — 그날 구매장(268호 제외)을 품목의 협력업체별로.
 *  인쇄는 업체마다 새 장, 엑셀은 업체별 시트. */
export function SupplierOrdersTab() {
  const [date, setDate] = useTabField('supplier-orders', 'date', today())
  const [supplierId, setSupplierId] = useTabField('supplier-orders', 'supplier', '')
  const { downloading, download } = useFileDownload()
  const { data: sups = [] } = useQuery({
    queryKey: ['purchase-suppliers'],
    queryFn: () => api<Supplier[]>('/api/purchase/suppliers'),
  })
  const qs = `date=${date}${supplierId ? `&supplier=${supplierId}` : ''}`
  const { data, isFetching } = useQuery({
    queryKey: ['supplier-orders', qs],
    queryFn: () => api<{ date: string; orders: SupplierOrder[] }>(`/api/purchase/supplier-orders?${qs}`),
    enabled: !!date,
  })
  const orders = data?.orders ?? []
  const supName = sups.find((s) => String(s.id) === supplierId)?.name

  return (
    <div id="sup-order-print" className="max-w-5xl mx-auto p-6">
      <style>{`@media print {
        body * { visibility: hidden !important; }
        #sup-order-print, #sup-order-print * { visibility: visible !important; }
        #sup-order-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
        #sup-order-print .no-print { display: none !important; }
        #sup-order-print .sup-page { break-after: page; page-break-after: always; border: 0 !important; box-shadow: none !important; }
        #sup-order-print .sup-page:last-child { break-after: auto; page-break-after: auto; }
        @page { size: A4 portrait; margin: 10mm; }
      }`}</style>
      <div className="no-print bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm mb-3">
        <label className="text-xs font-semibold text-slate-500">일자</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls + ' w-44 tabular-nums'}/>
        <label className="text-xs font-semibold text-slate-500">협력업체</label>
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={inputCls + ' w-40'}>
          <option value="">전체</option>
          {sups.filter((s) => s.kind === 'supplier').map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
        </select>
        <span className="ml-auto text-xs text-slate-400">
          {isFetching ? '조회 중…' : `${orders.length}개 업체`}
        </span>
        <button onClick={() => window.print()} disabled={!orders.length}
                className="flex items-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
          <Printer className="w-4 h-4"/> 인쇄
        </button>
        <button onClick={() => download(`/api/purchase/supplier-orders/export?${qs}`,
                                        `협력업체발주서${supName ? `_${supName}` : ''}_${date}.xlsx`)}
                disabled={downloading || !orders.length}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
      </div>
      <p className="no-print text-xs text-slate-400 mb-3">
        구매장(268호 제외) 품목을 협력업체별로 나눈 발주서입니다. 업체 지정·머리글은 구매장 &gt; 협력업체 탭에서 바꿉니다.
      </p>

      <div className="space-y-4">
        {orders.map((od) => (
          <div key={od.supplier.id} className="sup-page bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-baseline gap-3">
              <h3 className="text-lg font-bold text-slate-900">{od.supplier.title}</h3>
              <span className="ml-auto text-sm tabular-nums text-slate-600">{date}</span>
              <span className="text-sm text-slate-600">㈜그린푸드</span>
            </div>
            {od.supplier.send_method && <div className="no-print text-xs text-slate-400">전달: {od.supplier.send_method}</div>}
            {od.supplier.note && <div className="text-sm text-rose-600 whitespace-pre-line mt-1">{od.supplier.note}</div>}
            <table className="w-full text-sm mt-2 border-collapse [&_td]:border [&_td]:border-slate-300 [&_th]:border [&_th]:border-slate-400 [&_td]:px-2 [&_td]:py-0.5 [&_th]:px-2 [&_th]:py-1">
              <thead className="bg-slate-50 text-xs">
                <tr>
                  <th className="text-left">품명</th>
                  <th className="text-left w-40">특이사항</th>
                  <th className="text-right w-16">수량</th>
                  <th className="text-left w-12">단위</th>
                  <th className="text-left">업장</th>
                </tr>
              </thead>
              <tbody>
                {od.groups.map((g) => g.lines.map((l, i) => (
                  <tr key={`${g.purchase_item}-${i}`} className="break-inside-avoid">
                    {i === 0 && <td rowSpan={g.lines.length} className="font-semibold align-top">{g.purchase_item}</td>}
                    <td className="text-rose-600 text-xs">{l.note_all}</td>
                    {/* 회색 = 환산 못 한 소수 팩·0·음수(손으로 고칠 줄) */}
                    <td className={cn('text-right tabular-nums', l.shade && 'bg-slate-300 [print-color-adjust:exact]')}>
                      {formatNum(l.qty)}
                    </td>
                    <td>{l.unit}</td>
                    <td>{l.partner_name}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        ))}
        {!orders.length && !isFetching && (
          <div className="text-center py-16 text-slate-400 text-sm bg-white rounded-xl border border-slate-200">
            이 날짜의 협력업체 발주 품목이 없습니다.
          </div>
        )}
      </div>
    </div>
  )
}
